// =====================================================================
// formatDate.js — America/Aruba date/time formatter for admin surfaces.
//
// Backend + Mongo always store timestamps in UTC (ISO 8601 with a
// trailing `Z`). The Admin dashboard sees them as UTC by default which
// is meaningless to the Aruba-based ops team. This module owns the ONE
// canonical formatter every admin surface should use so we never end
// up with drifting inline `toLocaleString(...)` calls.
//
// Aruba runs on UTC-04:00 year-round (no DST). Intl handles this
// correctly via `timeZone: "America/Aruba"`.
// =====================================================================

// Locale-fixed formatter so the output is stable regardless of the
// browser's language ("Aug 25, 2026" not "25 aug. 2026"). "en-US" with
// hour12=true renders the requested "12:23 AM" style.
const _fmt = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/Aruba",
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

/**
 * formatOrderDateTime
 *
 * Renders a UTC ISO timestamp as "Aug 25, 2026 · 12:23 AM" in
 * America/Aruba. Returns "" for null / undefined / unparsable input so
 * callers never render "Invalid Date" to the ops team.
 *
 * @param {string|Date|number|null|undefined} value — anything Date(...) accepts
 * @returns {string}
 */
export const formatOrderDateTime = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  // Intl emits: "Aug 25, 2026, 12:23 AM". Replace the SECOND separator
  // (the ", " between the year and the time) with " · " while leaving
  // the intra-date "25, 2026" comma intact.
  return _fmt.format(d).replace(/,\s(\d{1,2}:\d{2}\s(?:AM|PM))$/, " · $1");
};
